import { useState } from 'react';
import type { BabelLang, CardMenuItem } from '../../shared/protocol';
import { cardData, cardTitle } from './CardRail';
import { bt, btCard } from '../../shared/babel/strings';

/** Card detail sheet — the deliberate two-step (tap → confirm Play) that guards
 *  a single-use card. Play forces a fresh GPS fix; if it fails the card is not
 *  consumed (§4.8.2). */
export function CardSheet({
  card,
  onPlay,
  onCancel,
  lang = null,
}: {
  card: CardMenuItem;
  onPlay: (c: CardMenuItem) => Promise<void>;
  onCancel: () => void;
  /** v16 BOLT-071: the seeker's active Babel language (null = English). */
  lang?: BabelLang | null;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="sheet-backdrop" onClick={() => !busy && onCancel()} data-testid="card-sheet">
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet__name">{cardTitle(card, lang)}</div>
        <div className="sheet__data">{cardData(card, lang)}</div>
        {/* v17 BOLT-074: the wire still carries English (frozen) — the client
            swaps the sheet copy by card id under a Babel curse. */}
        <div className="sheet__desc">{btCard(lang, card.id, 'effect', card.effect)}</div>
        <div className="sheet__actions">
          <button className="btn" disabled={busy} data-testid="card-cancel" onClick={onCancel}>
            {bt(lang, 'cancel')}
          </button>
          <button
            className="btn btn--signal"
            disabled={busy}
            data-testid="card-play"
            onClick={async () => {
              setBusy(true);
              try {
                await onPlay(card);
              } finally {
                setBusy(false);
              }
            }}
          >
            {busy
              ? bt(lang, 'gettingLocation')
              : card.family === 'thermometer'
                ? bt(lang, 'startWalking')
                : card.family === 'photo'
                  ? bt(lang, 'demandPhoto')
                  : bt(lang, 'play')}
          </button>
        </div>
      </div>
    </div>
  );
}
